import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import GroupIcon from '@mui/icons-material/Group';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { usePlatformStats } from '../../hooks/useAdmin';

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatRelativeDate(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtitle?: string;
  loading?: boolean;
}

function StatCard({ icon, label, value, subtitle, loading }: StatCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              mr: 1.5,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 13, sm: 14 } }}>
            {label}
          </Typography>
        </Box>
        {loading ? (
          <Skeleton variant="text" width="60%" height={40} />
        ) : (
          <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            {value}
          </Typography>
        )}
        {subtitle && !loading && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: 12, sm: 13 } }}>
            {subtitle}
          </Typography>
        )}
        {loading && <Skeleton variant="text" width="40%" />}
      </CardContent>
    </Card>
  );
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { data: stats, isLoading, error } = usePlatformStats();

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load platform stats. Please try again later.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
        Platform Overview
      </Typography>

      {/* Stats grid: 2 columns on mobile, 4 on desktop */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard
            icon={<PeopleIcon fontSize="small" />}
            label="Total Hosts"
            value={stats?.totalHosts ?? 0}
            subtitle={`${stats?.activeHosts ?? 0} active`}
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard
            icon={<EventIcon fontSize="small" />}
            label="Total Events"
            value={stats?.totalEvents ?? 0}
            subtitle={`${stats?.activeEvents ?? 0} active`}
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard
            icon={<GroupIcon fontSize="small" />}
            label="Total Guests"
            value={stats?.totalGuests ?? 0}
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard
            icon={<AttachMoneyIcon fontSize="small" />}
            label="Total Revenue"
            value={stats ? formatCurrency(stats.totalRevenueCents) : '$0.00'}
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Recent signups */}
      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Recent Signups
            </Typography>
            <Button
              size="small"
              onClick={() => navigate('/admin/hosts')}
              sx={{ minHeight: 44 }}
            >
              View all
            </Button>
          </Box>

          {isLoading ? (
            <Box>
              {[1, 2, 3, 4, 5].map((i) => (
                <Box key={i} sx={{ py: 1.5 }}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                </Box>
              ))}
            </Box>
          ) : stats?.recentSignups && stats.recentSignups.length > 0 ? (
            <List disablePadding>
              {stats.recentSignups.map((signup) => (
                <ListItem
                  key={signup.id}
                  disableGutters
                  sx={{
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xs: 14, sm: 16 } }}>
                        {signup.name}
                      </Typography>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontSize: { xs: 13, sm: 14 } }}>
                          {signup.email}
                        </Typography>
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: { xs: 12, sm: 13 } }}>
                          Joined {formatRelativeDate(signup.createdAt)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No recent signups.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
